import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EmployeeTable = ({ employees, onEditEmployee, onDeleteEmployee }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editValues, setEditValues] = useState({});

  const filteredEmployees = employees.filter(employee =>
    employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditStart = (employee) => {
    setEditingEmployee(employee.id);
    setEditValues({
      billingLimit: employee.billingLimit,
      role: employee.role
    });
  };

  const handleEditSave = (employeeId) => {
    onEditEmployee(employeeId, editValues);
    setEditingEmployee(null);
    setEditValues({});
  };

  const handleEditCancel = () => {
    setEditingEmployee(null);
    setEditValues({});
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'success', label: 'Active' },
      inactive: { color: 'error', label: 'Inactive' },
      pending: { color: 'warning', label: 'Pending' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-2 py-1 text-xs font-medium bg-${config.color}/10 text-${config.color} rounded-full`}>
        {config.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      manager: { color: 'primary', label: 'Manager' },
      staff: { color: 'secondary', label: 'Staff' },
      intern: { color: 'muted', label: 'Intern' }
    };
    
    const config = roleConfig[role] || roleConfig.staff;
    return (
      <span className={`px-2 py-1 text-xs font-medium bg-${config.color}/10 text-${config.color} rounded-full`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Search and Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 max-w-md">
          <Input
            type="search"
            placeholder="Search employees..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" iconName="Filter" iconPosition="left" iconSize={16}>
            Filter
          </Button>
          <Button variant="outline" iconName="Download" iconPosition="left" iconSize={16}>
            Export
          </Button>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block">
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Billing Limit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Total Billed
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-muted/25 micro-animation">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon name="User" size={16} className="text-primary" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-foreground">{employee.name}</div>
                          <div className="text-sm text-muted-foreground">{employee.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {editingEmployee === employee.id ? (
                        <select
                          value={editValues.role}
                          onChange={(e) => setEditValues({...editValues, role: e.target.value})}
                          className="text-sm border border-border rounded px-2 py-1"
                        >
                          <option value="staff">Staff</option>
                          <option value="manager">Manager</option>
                          <option value="intern">Intern</option>
                        </select>
                      ) : (
                        getRoleBadge(employee.role)
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(employee.status)}
                    </td>
                    <td className="px-6 py-4">
                      {editingEmployee === employee.id ? (
                        <input
                          type="number"
                          value={editValues.billingLimit}
                          onChange={(e) => setEditValues({...editValues, billingLimit: parseFloat(e.target.value)})}
                          className="text-sm border border-border rounded px-2 py-1 w-24"
                        />
                      ) : (
                        <span className="text-sm font-medium text-foreground">
                          ${employee?.billingLimit?.toFixed(2)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-foreground">${employee?.totalBilled?.toFixed(2)}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {editingEmployee === employee.id ? (
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditSave(employee.id)}
                            iconName="Check"
                            iconSize={14}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleEditCancel}
                            iconName="X"
                            iconSize={14}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditStart(employee)}
                            iconName="Edit2"
                            iconSize={14}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeleteEmployee(employee.id)}
                            iconName="Trash2"
                            iconSize={14}
                            className="text-error hover:text-error"
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {filteredEmployees.map((employee) => (
          <div key={employee.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground">{employee.name}</h3>
                  <p className="text-sm text-muted-foreground">{employee.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditStart(employee)}
                  iconName="Edit2"
                  iconSize={14}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDeleteEmployee(employee.id)}
                  iconName="Trash2"
                  iconSize={14}
                  className="text-error hover:text-error"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Role:</span>
                <div className="mt-1">{getRoleBadge(employee.role)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Status:</span>
                <div className="mt-1">{getStatusBadge(employee.status)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Billing Limit:</span>
                <div className="mt-1 font-medium text-foreground">${employee.billingLimit.toFixed(2)}</div>
              </div>
              <div>
                <span className="text-muted-foreground">Total Billed:</span>
                <div className="mt-1 font-medium text-foreground">${employee.totalBilled.toFixed(2)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredEmployees.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No employees found</h3>
          <p className="text-muted-foreground">
            {searchTerm ? "Try adjusting your search terms" : "Start by adding your first employee"}
          </p>
        </div>
      )}
    </div>
  );
};

export default EmployeeTable;